/**
 * @description Hook to fetch agent instances list.
 * This hook fetches the agent instances list and sets it in the app store such that it can be accessed anywhere inside the app.
 * Invoked only once when the app is loaded & user is authenticated.
 *
 * @returns void
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { listAgentInstances } from '@/services/DataService';
import { useAppStore } from '@/store/appStore';
import { AgentInstance } from '@/types/agent';
interface AgentInstancesResponse {
  agent_instances: AgentInstance[];
}

export const useAgentInstancesList = () => {
  const { isSignedIn, isLoaded, userId } = useAuth();
  const setAgentInstances = useAppStore(state => state.setAgentInstances);

  const isAuthenticated = isSignedIn && isLoaded && userId;

  const { data: agentInstanceData, isLoading } = useQuery<AgentInstancesResponse>({
    queryKey: ['agent-instances'],
    queryFn: listAgentInstances,
    enabled: !!isAuthenticated,
    // Override global setting to refetch when window regains focus
    // Critical for updating UI state (e.g., showNewAnalysisButton) after user returns from idle
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (agentInstanceData?.agent_instances && isAuthenticated) {
      setAgentInstances(agentInstanceData.agent_instances);
    }
  }, [agentInstanceData, setAgentInstances, isAuthenticated]);

  return { isLoading };
};

// Keep the old export for backward compatibility during transition
export const useAgentsList = useAgentInstancesList;
